import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { usePersistedFilter, FILTER_KEYS } from '../../../utils/filterPersistence';
import { DEFAULT_VIEW_MODE, DEFAULT_ROWS_PER_PAGE } from '../utils/locationConstants';
import { searchLocations, filterByType, getLocationTypeId, getLocationName, getLocationTypeName } from '../utils/locationTransformers';

/**
 * Hook for managing location filter state with persistence
 * Follows the same pattern as useTicketFilters.js
 */
export const useLocationFilters = () => {
  // Persisted filter states
  const [viewMode, setViewMode] = usePersistedFilter(FILTER_KEYS.LOCATION.VIEW_MODE, DEFAULT_VIEW_MODE);
  const [page, setPage] = usePersistedFilter(FILTER_KEYS.LOCATION.PAGE, 0);
  const [rawRowsPerPage, setRowsPerPage] = usePersistedFilter(FILTER_KEYS.LOCATION.ROWS_PER_PAGE, DEFAULT_ROWS_PER_PAGE);
  const rowsPerPage = (rawRowsPerPage === -1 || (rawRowsPerPage && rawRowsPerPage > 0)) ? rawRowsPerPage : DEFAULT_ROWS_PER_PAGE;
  const [typeFilter, setTypeFilter] = usePersistedFilter(FILTER_KEYS.LOCATION.TYPE_FILTER, '');
  const [searchFilter, setSearchFilter] = usePersistedFilter(FILTER_KEYS.LOCATION.SEARCH, '');
  const [sortBy, setSortBy] = usePersistedFilter(FILTER_KEYS.LOCATION.SORT_BY, 'friendlyName');
  const [sortOrder, setSortOrder] = usePersistedFilter(FILTER_KEYS.LOCATION.SORT_ORDER, 'asc');

  // Local search input state for debouncing
  const [searchInputValue, setSearchInputValue] = useState(searchFilter);
  const searchDebounceRef = useRef(null);

  // Handle search input with debounce
  const handleSearchInputChange = useCallback((value) => {
    setSearchInputValue(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchFilter(value);
      setPage(0);
    }, 300);
  }, [setSearchFilter, setPage]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleTypeFilterChange = useCallback((value) => {
    setTypeFilter(value);
    setPage(0);
  }, [setTypeFilter, setPage]);

  const handleSortChange = useCallback((field) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  }, [sortBy, sortOrder, setSortBy, setSortOrder, setPage]);

  const handleViewModeChange = useCallback((mode) => {
    if (mode) setViewMode(mode);
  }, [setViewMode]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, [setRowsPerPage, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(0);
    setTypeFilter('');
    setSearchFilter('');
    setSearchInputValue('');
    setSortBy('friendlyName');
    setSortOrder('asc');
  }, [setPage, setTypeFilter, setSearchFilter, setSortBy, setSortOrder]);

  /**
   * Apply filters and sorting to a flat array of locations
   * @param {Array} locations - Flat array of locations
   * @returns {Array} Filtered and sorted locations
   */
  const applyFilters = useCallback((locations) => {
    if (!locations) return [];

    let filtered = locations;

    // Apply type filter
    if (typeFilter) {
      filtered = filterByType(filtered, parseInt(typeFilter));
    }

    // Apply search
    if (searchFilter && searchFilter.trim()) {
      filtered = searchLocations(filtered, searchFilter);
    }

    // Apply sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'friendlyName':
          aVal = getLocationName(a).toLowerCase();
          bVal = getLocationName(b).toLowerCase();
          break;
        case 'locationType':
          aVal = getLocationTypeId(a) || 0;
          bVal = getLocationTypeId(b) || 0;
          break;
        case 'parentName':
          aVal = (a.parentName || '').toLowerCase();
          bVal = (b.parentName || '').toLowerCase();
          break;
        case 'deviceCount':
          aVal = a.deviceCount || 0;
          bVal = b.deviceCount || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }
      if (sortOrder === 'desc') {
        return bVal < aVal ? -1 : bVal > aVal ? 1 : 0;
      }
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });

    return sorted;
  }, [typeFilter, searchFilter, sortBy, sortOrder]);

  /**
   * Paginate a filtered array
   */
  const paginate = useCallback((locations) => {
    if (rowsPerPage === -1) return locations;
    return locations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage]);

  const hasActiveFilters = useMemo(() => {
    return typeFilter !== '' || (searchFilter && searchFilter.trim() !== '');
  }, [typeFilter, searchFilter]);

  return {
    // State
    viewMode,
    page,
    rowsPerPage,
    typeFilter,
    searchFilter,
    searchInputValue,
    sortBy,
    sortOrder,
    hasActiveFilters,

    // Actions
    handleViewModeChange,
    handleSearchInputChange,
    handleTypeFilterChange,
    handleSortChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleResetFilters,

    // Processors
    applyFilters,
    paginate
  };
};

export default useLocationFilters;
